/* Tutor (02 §3.5, §2.9).
 *
 * Two views in one screen: the thread list, and one open thread.
 *
 * The context chip is not decoration — `02 §3.5` requires the learner to be able
 * to see exactly what the Tutor was given. It names the anchor Unit, the mode,
 * and states plainly that a learner-state digest travels with every message.
 *
 * The reply streams in token by token. A partial reply stays on screen if the
 * connection drops: the server has already persisted it and ledgered its tokens,
 * so wiping it here would be a lie about what happened.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api, ApiFailure, streamTutorMessage,
  type BudgetBlock, type TutorMessage, type TutorMode, type TutorThreadDetail, type TutorThreadSummary,
} from '../api';
import { Markdown } from '../markdown';

const MODES: { value: TutorMode; label: string; blurb: string }[] = [
  { value: 'explain', label: 'Explain', blurb: 'Ask about the thing you just read.' },
  { value: 'socratic', label: 'Work it out', blurb: 'Guided by questions, not answers.' },
  { value: 'quiz_me', label: 'Quiz me', blurb: 'Practice questions. Never scored, never recorded.' },
  { value: 'review', label: 'Review', blurb: 'What the week says about your calibration.' },
];

function uuidV4(): string {
  const c: Crypto = crypto;
  if (typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  c.getRandomValues(b);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const hex = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function TutorScreen({ onOpenSettings }: { onOpenSettings?: () => void }) {
  const [threads, setThreads] = useState<TutorThreadSummary[] | null>(null);
  const [budget, setBudget] = useState<BudgetBlock | null>(null);
  const [open, setOpen] = useState<TutorThreadDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await api.tutorThreads();
      setThreads(list.items);
      setBudget(list.budget);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiFailure
          ? err.code === 'OFFLINE'
            ? 'offline'
            : err.message
          : 'Could not load the Tutor.',
      );
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const start = useCallback(async (mode: TutorMode) => {
    setBusy(true);
    try {
      const created = await api.createTutorThread({ mode });
      setOpen({ ...created, messages: [], budget: budget ?? { month: '', spent_eur: 0, cap_eur: 0, pct: 0 } });
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : 'Could not start a thread.');
    } finally {
      setBusy(false);
    }
  }, [budget]);

  const openThread = useCallback(async (id: string) => {
    setBusy(true);
    try {
      setOpen(await api.tutorThread(id));
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : 'Could not open that thread.');
    } finally {
      setBusy(false);
    }
  }, []);

  if (open) {
    return (
      <ThreadView
        thread={open}
        onBack={() => { setOpen(null); void load(); }}
        onBudget={setBudget}
        onOpenSettings={onOpenSettings}
      />
    );
  }

  return (
    <main className="screen">
      <p className="eyebrow">Tutor</p>
      <h1>Ask</h1>

      {budget ? <BudgetMeter budget={budget} /> : null}

      {error === 'offline' ? (
        <div className="card card--blocked">
          <div className="card__title"><span>Offline</span></div>
          <p style={{ margin: 0 }}>
            The Tutor needs a connection — it thinks on the server, not on this
            phone. Drills, lessons and quizzes all still work offline.
          </p>
        </div>
      ) : error ? (
        <p className="error">{error}</p>
      ) : null}

      <div className="card">
        <div className="card__title"><span>Start a thread</span></div>
        <div className="modes">
          {MODES.map((m) => (
            <button key={m.value} type="button" className="mode" disabled={busy} onClick={() => void start(m.value)}>
              <span className="mode__label">{m.label}</span>
              <span className="mode__blurb">{m.blurb}</span>
            </button>
          ))}
        </div>
      </div>

      <h3 className="sheet__h">Earlier</h3>
      {threads === null ? (
        <div className="skeleton skeleton--line" />
      ) : threads.length === 0 ? (
        <p className="hint">Nothing yet. The Tutor coaches — it never writes your content.</p>
      ) : (
        <ul className="unitlist">
          {threads.map((t) => (
            <li key={t.thread_id}>
              <button type="button" className="unitlist__btn" onClick={() => void openThread(t.thread_id)}>
                <span className="unitlist__type">{MODES.find((m) => m.value === t.mode)?.label ?? t.mode}</span>
                <span className="unitlist__title">{t.title}</span>
                <span className="unitlist__meta">
                  {t.message_count ?? 0} {t.message_count === 1 ? 'message' : 'messages'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="spacer" />
      <p className="footnote">
        The Tutor has no tools and no way to change anything: it cannot write
        content, alter your progress, or see your settings. Its only output is
        the text of its reply.
      </p>
    </main>
  );
}

function BudgetMeter({ budget }: { budget: BudgetBlock }) {
  return (
    <div className="card">
      <div className="card__title">
        <span>This month</span>
        <span className="card__meta">{budget.pct}%</span>
      </div>
      <div className="progressbar" aria-hidden="true">
        <span className="progressbar__fill" style={{ width: `${Math.min(100, budget.pct)}%` }} />
      </div>
      {/* Honesty, not decoration: under CR-019 the Tutor runs on a subscription,
        * so this figure is usage measured in notional API prices — not money
        * leaving an account. Presenting it as a bill would be a lie. */}
      <p className="hint" style={{ marginTop: 0 }}>
        {budget.spent_eur.toFixed(2)} of {budget.cap_eur.toFixed(2)} — a usage meter, not a bill.
        The Tutor runs on your Claude subscription, so a reply costs no extra money;
        this tracks how hard you are leaning on it.
      </p>
    </div>
  );
}

function ThreadView({
  thread, onBack, onBudget, onOpenSettings,
}: {
  thread: TutorThreadDetail;
  onBack: () => void;
  onBudget: (b: BudgetBlock) => void;
  onOpenSettings?: (() => void) | undefined;
}) {
  const [messages, setMessages] = useState<TutorMessage[]>(thread.messages);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [partial, setPartial] = useState('');
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const [showContext, setShowContext] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages.length, partial]);

  const send = useCallback(async () => {
    const content = draft.trim();
    if (!content || streaming) return;

    const messageId = uuidV4();
    setMessages((prev) => [
      ...prev,
      { message_id: messageId, role: 'user', content_md: content, created_at: new Date().toISOString() },
    ]);
    setDraft('');
    setStreaming(true);
    setPartial('');
    setError(null);

    let text = '';
    await streamTutorMessage(thread.thread_id, messageId, content, {
      onStart: () => { /* the assistant bubble appears with the first delta */ },
      onDelta: (chunk) => { text += chunk; setPartial(text); },
      onDone: (info) => {
        setMessages((prev) => [
          ...prev,
          { message_id: `${messageId}-a`, role: 'assistant', content_md: text, created_at: new Date().toISOString() },
        ]);
        setPartial('');
        if (typeof info.month_spent_eur === 'number' && typeof info.month_cap_eur === 'number') {
          onBudget({
            month: '', spent_eur: info.month_spent_eur, cap_eur: info.month_cap_eur,
            pct: info.month_cap_eur > 0 ? Math.round((info.month_spent_eur / info.month_cap_eur) * 100) : 0,
          });
        }
      },
      onError: (code, message) => {
        // Keep whatever streamed: the server persisted it and ledgered it.
        if (text) {
          setMessages((prev) => [
            ...prev,
            { message_id: `${messageId}-a`, role: 'assistant', content_md: text, created_at: new Date().toISOString() },
          ]);
        }
        setPartial('');
        setError({ code, message });
      },
    });
    setStreaming(false);
  }, [draft, streaming, thread.thread_id, onBudget]);

  return (
    <main className="screen tutor">
      <button type="button" className="back" onClick={onBack}>← Threads</button>
      <p className="eyebrow">{MODES.find((m) => m.value === thread.mode)?.label ?? thread.mode}</p>
      <h1>{thread.title}</h1>

      {/* The context chip: exactly what the Tutor can see (02 §3.5). */}
      <button type="button" className="sources__toggle" onClick={() => setShowContext((v) => !v)}>
        What can the Tutor see? {showContext ? '▾' : '▸'}
      </button>
      {showContext ? (
        <div className="card">
          <ul className="kv">
            <li><span>Mode</span><span>{thread.mode}</span></li>
            <li><span>Anchored to</span><span>{thread.unit_id ?? 'nothing in particular'}</span></li>
            <li><span>Your progress</span><span>a summary digest</span></li>
            <li><span>This thread</span><span>last 10 messages</span></li>
          </ul>
          <p className="hint">
            The digest is Altitude, mastery counts, eroding Topics, calibration
            flags and your last few Quiz scores — all of it already on your
            Progress screen. Assessment questions are never sent, from any thread.
          </p>
        </div>
      ) : null}

      <div className="chat">
        {/* An empty bubble is never worth rendering: it reads as a broken layout
          * rather than as the absence of a reply. */}
        {messages.filter((m) => m.content_md.trim().length > 0).map((m) => (
          <div key={m.message_id} className={`bubble bubble--${m.role}`}>
            {m.role === 'assistant' ? <Markdown source={m.content_md} /> : <p>{m.content_md}</p>}
          </div>
        ))}
        {partial ? (
          <div className="bubble bubble--assistant"><Markdown source={partial} /></div>
        ) : streaming ? (
          <div className="bubble bubble--assistant"><p className="hint">Thinking…</p></div>
        ) : null}
        <div ref={endRef} />
      </div>

      {error ? (
        <div className="card card--blocked">
          <div className="card__title"><span>{error.code === 'BUDGET_EXCEEDED' ? 'Budget reached' : 'The Tutor stopped'}</span></div>
          <p style={{ margin: 0 }}>{error.message}</p>
          {error.code === 'BUDGET_EXCEEDED' && onOpenSettings ? (
            <button type="button" style={{ marginTop: '0.875rem' }} onClick={onOpenSettings}>Open Settings</button>
          ) : null}
        </div>
      ) : null}

      <div className="composer">
        <textarea
          className="runner__editor runner__editor--short"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask something"
          rows={3}
          disabled={streaming}
        />
        <button type="button" onClick={() => void send()} disabled={streaming || draft.trim().length === 0}>
          {streaming ? 'Answering…' : 'Send'}
        </button>
      </div>
    </main>
  );
}
